import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import Header from "../../common/Header";
import ArrayStore from "devextreme/data/array_store";
import DataGrid, {
  Toolbar,
  Column,
  Lookup,
  LoadPanel,
  Scrolling,
  Item,
  SearchPanel,
  HeaderFilter,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { topicData, categoryData, statusData } from "../data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessage } from "@fortawesome/free-regular-svg-icons";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import * as filterXmark from "./filterXmark.svg";
import styled from "styled-components";
import { Template } from "devextreme-react";
import StatBox from "./StatBox";

const QnABoardWrapper = styled.main`
  #qnaGrid {
    margin: auto;
    height: calc(100vh - 220px);
    max-width: 99vw;
  }
  #qnaGrid .dx-datagrid-header-panel {
    padding: 0px;
    margin-bottom: 16px;
  }
  #qnaGrid .dx-toolbar-before {
    padding: 0px;
    width: 100%;
  }
  #qnaGrid .dx-toolbar-button:first-child {
    width: 100%;
  }
  #qnaGrid .dx-toolbar .dx-texteditor {
    width: 100% !important;
    height: 36px !important;
  }
  #qnaGrid .dx-toolbar-item {
    padding: 0;
  }
  #qnaGrid .dx-datagrid-search-panel {
    margin-left: 0px;
  }
  #qnaGrid .dx-button {
    margin: 0px;
    margin-left: 16px;
    border: 3px solid #7fba4c;
    border-radius: 10px;
    padding: 3px 0px;
    span {
      font-weight: bold;
    }
  }
  #qnaGrid .dx-button-mode-outlined {
    color: black;
  }
  #qnaGrid .dx-button-mode-contained {
    background-color: #7fba4c;
    color: white;
    font-weight: 600;
    &:hover {
      background-color: #007048;
      border: 3px solid #007048;
    }
    transition: 0.2s ease-in-out;
  }
  #qnaGrid .dx-button-has-icon {
    border-radius: 10px !important;
    padding: 2px;
  }
  #qnaGrid
    .dx-data-row.dx-state-hover:not(.dx-selection):not(.dx-row-inserted):not(
      .dx-row-removed
    ):not(.dx-edit-row)
    > td:not(.dx-focused) {
    background-color: #007048 !important;
    color: #fff !important;
    transition: 0.2s ease-in-out;
  }
  .dx-texteditor.dx-state-focused::before {
    border-bottom: 2px solid #007048 !important;
  }
`;

const QnABoard = () => {
  const userInfo = useSelector((state) => state.userInfo);
  const [boardData, setBoardData] = useState(null);
  const [isMyQs, setMyQs] = useState(true);
  const navigate = useNavigate();
  const dataGridRef = useRef(null);
  const allQsStatData = useRef(null);
  const myQsStatData = useRef(null);

  const alignHeaderCenter = (e) => {
    if (e.rowType === "header") {
      e.cellElement.style.textAlign = "center";
    }
  };

  const formatCmt = (cellData) => {
    return (
      <div>
        <FontAwesomeIcon icon={faMessage} style={{ marginRight: "5px" }} />
        {cellData.value}
      </div>
    );
  };

  const formatStatusCell = (cellData) => {
    const [hex, text] = cellData.displayValue.split(",");
    return (
      <div>
        <FontAwesomeIcon
          icon={faCircle}
          style={{ color: hex, marginRight: "5px" }}
        />
        {text}
      </div>
    );
  };

  const handleClearFilter = () => {
    setMyQs(false);
    dataGridRef.current.instance.clearFilter();
  };

  const handleFilterQs = () => {
    if (isMyQs) {
      setMyQs(false);
      dataGridRef.current.instance.clearFilter();
    } else {
      setMyQs(true);
      dataGridRef.current.instance.option("filterValue", [
        "author",
        "=",
        `${userInfo.name} (${userInfo.employeeNo})`,
      ]);
    }
  };

  const handleCreateRequest = () => {
    navigate("/qna/new");
  };

  const handleDisplayStatus = (statusData) => {
    return `${statusData.hex},${statusData.text}`;
  };

  const handleRenderStatusFilter = (data) => {
    return (
      <div>
        <FontAwesomeIcon
          icon={faCircle}
          style={{ marginRight: "5px", color: `${data.hex}` }}
        />
        {data.text}
      </div>
    );
  };

  const handleRowClick = (e) => {
    navigate(`/qna/${e.key}`);
  };

  const modAuthorName = (rowData) => {
    return `${rowData.auName} (${rowData.au})`;
  };

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BECCO_API_URL}/api/bbs`)
      .then((res) => {
        const allQs = [0, 0, 0, 0, 0];
        const myQs = [0, 0, 0, 0, 0];
        res.data.forEach((ele) => {
          if (ele.au === userInfo.employeeNo) {
            myQs[0] += 1;
            myQs[ele.status] += 1;
          }
          allQs[0] += 1;
          allQs[ele.status] += 1;
        });
        allQsStatData.current = allQs;
        myQsStatData.current = myQs;
        return res.data;
      })
      .then((data) => {
        setBoardData(
          new ArrayStore({
            key: "id",
            data,
          })
        );
      })
      .catch((err) => {
        throw err;
      });
  }, []);

  return (
    <React.Fragment>
      <Header title={"QnA"} menuDataSource={null} />
      <QnABoardWrapper>
        {boardData && (
          <StatBox
            allQsStatData={allQsStatData.current}
            isMyQs={isMyQs}
            myQsStatData={myQsStatData.current}
          />
        )}
        <DataGrid
          dataSource={boardData}
          defaultFilterValue={[
            "author",
            "=",
            `${userInfo.name} (${userInfo.employeeNo})`,
          ]}
          filterSyncEnabled={true}
          id="qnaGrid"
          hoverStateEnabled={true}
          showBorders={true}
          showColumnHeaders={true}
          showColumnLines={true}
          onCellPrepared={alignHeaderCenter}
          onRowClick={handleRowClick}
          ref={dataGridRef}
          width={1520}
        >
          <SearchPanel visible={true} placeholder="Search..." />
          <LoadPanel enabled={true} />
          <Scrolling mode="virtual" showScrollbar={"always"} />
          <HeaderFilter visible={true} />
          <Toolbar>
            <Item name="searchPanel" location="before" />
            <Item location="before">
              <Button
                id="clearFilterBtn"
                icon={filterXmark.default}
                width={36}
                height={36}
                stylingMode="contained"
                onClick={handleClearFilter}
              />
            </Item>
            <Item location="before">
              <Button
                id="filterQsBtn"
                text={!isMyQs ? "나의 문의사항" : "전체글"}
                width={158}
                height={36}
                stylingMode={!isMyQs ? "outlined" : "contained"}
                onClick={handleFilterQs}
              />
            </Item>
            <Item location="before">
              <Button
                id="createRequest"
                text="문의하기"
                width={158}
                height={36}
                stylingMode="contained"
                onClick={handleCreateRequest}
              />
            </Item>
          </Toolbar>
          <Column
            dataField="status"
            caption="Status"
            alignment="center"
            dataType="string"
            width={97}
            cellRender={formatStatusCell}
          >
            <Lookup
              dataSource={statusData}
              valueExpr="value"
              displayExpr={handleDisplayStatus}
            />
            <HeaderFilter dataSource={statusData} allowSearch={true} />
          </Column>
          <Column
            dataField="topic"
            caption="Topic"
            alignment="center"
            dataType="string"
            width={94}
          >
            <Lookup
              dataSource={topicData}
              valueExpr="value"
              displayExpr="text"
            />
            <HeaderFilter dataSource={topicData} allowSearch={true} />
          </Column>
          <Column
            dataField="cat"
            caption="Category"
            alignment="center"
            dataType="string"
            width={201}
          >
            <Lookup
              dataSource={categoryData}
              valueExpr="value"
              displayExpr="text"
            />
            <HeaderFilter dataSource={categoryData} allowSearch={true} />
          </Column>
          <Column
            dataField="title"
            caption="Title"
            alignment="left"
            dataType="string"
            width={549}
          >
            <HeaderFilter allowSearch={true} />
          </Column>
          <Column
            dataField="author"
            caption="Author"
            alignment="center"
            dataType="string"
            width={194}
            calculateCellValue={modAuthorName}
          >
            <HeaderFilter allowSearch={true} />
          </Column>
          <Column
            dataField="sbmDate"
            caption="Submit Date"
            alignment="center"
            dataType="date"
            width={153}
            format="yyyy-MM-dd"
          >
            <HeaderFilter allowSearch={true} />
          </Column>
          <Column
            dataField="cpltDate"
            caption="Complete Date"
            alignment="center"
            dataType="date"
            width={153}
            format="yyyy-MM-dd"
          >
            <HeaderFilter allowSearch={true} />
          </Column>
          <Column
            dataField="nCmts"
            caption=""
            alignment="center"
            dataType="number"
            cellRender={formatCmt}
            width={79}
          >
            <HeaderFilter allowSearch={true} />
          </Column>
          <Template
            name="statusHeaderFilter"
            render={handleRenderStatusFilter}
          />
        </DataGrid>
      </QnABoardWrapper>
    </React.Fragment>
  );
};

export default QnABoard;
